import React, { Suspense } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Container,
  CopyToClipboard,
  Grid,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
} from "@amzn/awsui-components-react";
import { Differ, DiffResult, Viewer } from "json-diff-kit";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { CheckboxProps } from "@amzn/awsui-components-react/polaris/checkbox/interfaces";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../../types/documents";
import { AccountingEventsGroup } from "../../types/events";
import { CanonicalBusinessEvent } from "../../types/cbes";
import "json-diff-kit/dist/viewer.css";
import "json-diff-kit/viewer-monokai.css";
import {removeKeysFromJson, sortedStringify} from "../../helpers/jsonUtils";
import {
  isAccountingEventsGroup,
  isCanonicalBusinessEvent,
} from "../../helpers/typeChecks";
import { groupCBEByUsecase } from "../../helpers/cbeUtils";

export interface JsonCompareComponentProps {
  isModalVisible: boolean;
  objects: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent
  )[];
}

function getAvailableDestinations(
  objects: CanonicalBusinessEvent[],
  usecase: string | undefined
): Set<string> {
  const destinations = new Set<string>();

  if (!usecase) {
    return destinations;
  }

  objects.forEach((cbe) => {
    if (cbe.usecaseName === usecase) {
      destinations.add(cbe.destination);
    }
  });

  return destinations;
}

const createUsecaseOptions = (
  cbes: CanonicalBusinessEvent[]
): SelectProps.Option[] => {
  const groupedCBEs = groupCBEByUsecase(cbes);
  return Array.from(groupedCBEs.keys()).map((usecase) => ({
    value: usecase,
    label: usecase,
  }));
};

const createVersionOptions = (
  objects: any[],
  usecase?: string | undefined,
  destination?: string | undefined
): SelectProps.Option[] => {
  if (!objects?.length) return [];

  if (isCanonicalBusinessEvent(objects[0]) && usecase) {
    const filteredObjects = filterObjects(objects, usecase, destination);

    return filteredObjects.map((_, index) => ({
      label: `Version ${index + 1}`,
      value: index.toString(),
    }));
  }

  const adder = "accountingEvents" in objects[0] ? 1 : 0; // DBD starts at v0
  return objects.map((_, index) => ({
    label: `Version ${index + adder}`,
    value: index.toString(),
  }));
};

const filterObjects = (
  objects: any[],
  usecase?: string | undefined,
  destination?: string | undefined
): any[] => {
  if (!objects?.length) return [];

  if (isCanonicalBusinessEvent(objects[0]) && usecase) {
    const filteredObjects = objects.filter(
      (cbe): cbe is CanonicalBusinessEvent => {
        const matches =
          cbe !== undefined &&
          cbe.usecaseName === usecase &&
          cbe.destination === destination;

        return matches;
      }
    );

    return filteredObjects;
  }

  return objects;
};

const JsonCompareComponent: React.FC<JsonCompareComponentProps> = ({
  isModalVisible,
  objects,
}) => {
  const isAccountingGroup =
    objects.length > 0 && isAccountingEventsGroup(objects[0]);
  const isCbe = objects.length > 0 && isCanonicalBusinessEvent(objects[0]);

  const usecaseOptions = React.useMemo(() => {
    if (isCbe && !isAccountingGroup) {
      return createUsecaseOptions(objects as CanonicalBusinessEvent[]);
    }
    return [];
  }, [objects, isCbe, isAccountingGroup]);

  const [versionOptions, setVersionOptions] = React.useState<
    OptionDefinition[]
  >(createVersionOptions(objects));
  const destinationOptions: SelectProps.Option[] = [
    { label: "Flare", value: "FLARE" },
    { label: "rACE", value: "RACE" },
    { label: "None", value: "NONE" },
  ];

  const differ = new Differ({ arrayDiffMethod: "lcs" });
  const [diff, setDiff] = React.useState<[DiffResult[], DiffResult[]]>();
  const [diffUnchangedLinesOptions, setDiffUnchangedLinesOptions] =
    React.useState<boolean | object>(true);

  const [rightSelectedVersion, setRightSelectedVersion] =
    React.useState<SelectProps.Option>(
      versionOptions[versionOptions.length - 1]
    );
  const [leftSelectedVersion, setLeftSelectedVersion] =
    React.useState<SelectProps.Option>(
      versionOptions.length > 1
        ? versionOptions[versionOptions.length - 2]
        : rightSelectedVersion
    );
  const [selectedUsecase, setSelectedUsecase] =
    React.useState<SelectProps.Option | null>(
      usecaseOptions.length > 0 ? usecaseOptions[0] : null
    );
  const [selectedDestination, setSelectedDestination] =
    React.useState<SelectProps.Option>(destinationOptions[0]);

  function handleDiffCheck() {
    const leftIndex = Number(leftSelectedVersion.value);
    const rightIndex = Number(rightSelectedVersion.value);

    // Remove raw_payload and publishStatus to improve readability
    const leftObject = removeKeysFromJson(
      filteredObjects[leftIndex],
      ["raw_payload", "publishStatus"]
    );
    const rightObject = removeKeysFromJson(
      filteredObjects[rightIndex],
      ["raw_payload", "publishStatus"]
    );

    const diff = differ.diff(leftObject, rightObject);
    // @ts-ignore
    setDiff(diff);
  }

  const handleSelectChange = React.useCallback(
    (setter: (option: SelectProps.Option) => void, name: string) =>
      ({ detail }: { detail: { selectedOption: SelectProps.Option } }) => {
        setter(detail.selectedOption);
      },
    []
  );

  function handleShowHideLines() {
    if (diffUnchangedLinesOptions === false) {
      setDiffUnchangedLinesOptions({
        threshold: 5,
        margin: 3,
        expandMoreLinesLimit: 5,
      });
    } else {
      setDiffUnchangedLinesOptions(false);
    }
  }

  // Get filtered destination options based on selected usecase
  const getDestinationOptions = React.useCallback(() => {
    if (!selectedUsecase?.value || !isCbe) {
      return destinationOptions;
    }

    const availableDestinations = getAvailableDestinations(
      objects as CanonicalBusinessEvent[],
      selectedUsecase.value
    );

    // Filter the destination options to only include those that exist in the data
    return destinationOptions.filter((option) =>
      availableDestinations.has(option.value || "")
    );
  }, [selectedUsecase, objects, isCbe, destinationOptions]);

  // Only show valid destinations for selected usecase in dropdown
  React.useEffect(() => {
    if (selectedUsecase?.value && isCbe) {
      const availableDestinations = getAvailableDestinations(
        objects as CanonicalBusinessEvent[],
        selectedUsecase.value
      );

      // If currently selected destination is not available for this usecase, change to first valid one
      if (
        selectedDestination &&
        !availableDestinations.has(selectedDestination.value || "")
      ) {
        const newDestOptions = getDestinationOptions();
        if (newDestOptions.length > 0) {
          setSelectedDestination(newDestOptions[0]);
        }
      }
    }
  }, [selectedUsecase, objects, isCbe]);

  // Update usecase when objects change
  React.useEffect(() => {
    if (
      isCbe &&
      !isAccountingGroup &&
      usecaseOptions.length > 0 &&
      !selectedUsecase
    ) {
      setSelectedUsecase(usecaseOptions[0]);
    } else if ((!isCbe || isAccountingGroup) && selectedUsecase) {
      setSelectedUsecase(null);
    }
  }, [isCbe, isAccountingGroup, usecaseOptions, selectedUsecase]);

  const [filteredObjects, setFilteredObjects] = React.useState<any[]>(objects);

  React.useEffect(() => {
    if (isCbe && !isAccountingGroup) {
      const filtered = filterObjects(
        objects,
        selectedUsecase?.value || undefined,
        selectedDestination?.value || undefined
      );

      setFilteredObjects(filtered);

      const newOptions = createVersionOptions(
        filtered,
        selectedUsecase?.value || undefined,
        selectedDestination?.value || undefined
      );

      setVersionOptions(newOptions);
    } else {
      setFilteredObjects(objects);
      setVersionOptions(createVersionOptions(objects));
    }
  }, [objects, selectedUsecase, selectedDestination, isCbe, isAccountingGroup]);

  React.useEffect(() => {
    if (versionOptions.length > 0) {
      setRightSelectedVersion(versionOptions[versionOptions.length - 1]);
      setLeftSelectedVersion(
        versionOptions.length > 1
          ? versionOptions[versionOptions.length - 2]
          : versionOptions[versionOptions.length - 1]
      );
    }
  }, [versionOptions]);

  // Check if same version is selected in both dropdowns
  const isSameVersionSelected =
    leftSelectedVersion &&
    rightSelectedVersion &&
    leftSelectedVersion.value === rightSelectedVersion.value &&
    versionOptions.length > 1;

  // Trigger diff check when versions change
  React.useEffect(() => {
    if (leftSelectedVersion && rightSelectedVersion) {
      handleDiffCheck();
    }
  }, [leftSelectedVersion, rightSelectedVersion]);

  if (!isModalVisible) {
    return null;
  }

  const showUsecaseSelector =
    isCbe && !isAccountingGroup && usecaseOptions.length > 0 && selectedUsecase;

  return (
    <Suspense fallback={<div>Loading content...</div>}>
      <Container>
        <Grid
          gridDefinition={[
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 2 },
            { colspan: 2 },
            { colspan: 3 },
            { colspan: 2 },
            { colspan: 12 },
          ]}
        >
          <div />
          {showUsecaseSelector && (
            <>
              <Select
                selectedOption={selectedUsecase}
                onChange={handleSelectChange(setSelectedUsecase, "Usecase")}
                options={usecaseOptions}
              />
              <Select
                selectedOption={selectedDestination}
                onChange={handleSelectChange(
                  setSelectedDestination,
                  "Destination"
                )}
                options={getDestinationOptions()}
                disabled={getDestinationOptions().length <= 1}
              />
            </>
          )}
          {!showUsecaseSelector && (
            <>
              <div />
              <div />
            </>
          )}
          <div />
          <Select
            selectedOption={leftSelectedVersion}
            onChange={handleSelectChange(
              setLeftSelectedVersion,
              "Left Version"
            )}
            options={versionOptions}
            disabled={versionOptions.length <= 1}
          />
          <div>
            <Button
              onClick={() => {
                const n = Number(leftSelectedVersion.value);
                setLeftSelectedVersion(versionOptions[n - 1]);
                setRightSelectedVersion(versionOptions[n]);
                return null;
              }}
              disabled={Number(leftSelectedVersion.value) === 0}
            >
              Previous
            </Button>
            <Button
              onClick={() => {
                const n = Number(leftSelectedVersion.value);
                setLeftSelectedVersion(versionOptions[n + 1]);
                setRightSelectedVersion(versionOptions[n + 2]);
                return null;
              }}
              disabled={
                Number(leftSelectedVersion.value) + 2 >= versionOptions.length
              }
            >
              Next
            </Button>
            <CopyToClipboard
              copyButtonText="Copy JSON"
              copySuccessText="Copied to clipboard"
              copyErrorText="Error copying"
              textToCopy={sortedStringify(filteredObjects[Number(leftSelectedVersion.value)], 2)}
            />
          </div>
          <Button
            onClick={handleShowHideLines}
            disabled={versionOptions.length <= 1}
          >
            {diffUnchangedLinesOptions === false
              ? "Hide unchanged lines"
              : "Show unchanged lines"}
          </Button>
          <Select
            selectedOption={rightSelectedVersion}
            onChange={handleSelectChange(
              setRightSelectedVersion,
              "Right Version"
            )}
            options={versionOptions}
            disabled={versionOptions.length <= 1}
          />
          <CopyToClipboard
            copyButtonText="Copy JSON"
            copySuccessText="Copied to clipboard"
            copyErrorText="Error copying"
            textToCopy={sortedStringify(filteredObjects[Number(rightSelectedVersion.value)], 2)}
          />
          {versionOptions.length <= 1 && (
            <Alert> Only one version exists, nothing to compare.</Alert>
          )}
          {isSameVersionSelected && (
            <Alert type="warning">
              {" "}
              Same versions selected for comparison - nothing to compare.
            </Alert>
          )}
          {diff && versionOptions.length > 1 && !isSameVersionSelected && (
            <Viewer
              diff={diff}
              lineNumbers
              highlightInlineDiff
              hideUnchangedLines={diffUnchangedLinesOptions}
              inlineDiffOptions={{
                mode: "word",
                wordSeparator: " ",
              }}
              syntaxHighlight={{
                theme: "monokai",
              }}
              virtual={false}
            />
          )}
        </Grid>
      </Container>
    </Suspense>
  );
};

export default JsonCompareComponent;
